// src/components/InventoryOrderAlerts.js
// 叫貨頁提醒區：未點貨 / 未叫貨 / 未達低消

import React, { useCallback, useEffect, useState } from 'react';
import socket from '../lib/socket';

const api = (url, opts) => fetch(url, { headers: { 'Content-Type': 'application/json' }, ...opts }).then(r => r.json());

const WD = ['日', '一', '二', '三', '四', '五', '六'];

function formatYmdLabel(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || '';
  const [y, m, d] = ymd.split('-').map(Number);
  const wd = new Date(y, m - 1, d).getDay();
  return `${m}/${d}(${WD[wd]})`;
}

function formatMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return x.toLocaleString('zh-TW', { maximumFractionDigits: 0 });
}

/**
 * @param {{ operator: string, belowMin?: Array<{vendor_id, vendor, amount, min_order_amount, missing_price}> }} props
 */
export default function InventoryOrderAlerts({ operator, belowMin = [] }) {
  const [uncounted, setUncounted] = useState([]);
  const [unordered, setUnordered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ackingAll, setAckingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api('/api/inventory/order-alerts');
      if (res.success) {
        setUncounted(res.uncounted || []);
        setUnordered(res.unordered || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refresh = () => load();
    socket.on('inventory:record', refresh);
    socket.on('inventory:order-check', refresh);
    socket.on('inventory:items', refresh);
    return () => {
      socket.off('inventory:record', refresh);
      socket.off('inventory:order-check', refresh);
      socket.off('inventory:items', refresh);
    };
  }, [load]);

  const ack = async (row) => {
    const name = String(operator || '').trim();
    if (!name) {
      alert('請先填寫操作者姓名');
      return;
    }
    try {
      const res = await api('/api/inventory/order-alerts/ack', {
        method: 'POST',
        body: JSON.stringify({
          order_date: row.order_date,
          vendor_id: row.vendor_id,
          operator: name,
        }),
      });
      if (res.success) load();
      else alert(res.error || '處理失敗');
    } catch (e) {
      alert('處理失敗');
    }
  };

  const ackAll = async () => {
    const name = String(operator || '').trim();
    if (!name) {
      alert('請先填寫操作者姓名');
      return;
    }
    if (!unordered.length) return;
    if (!window.confirm(`確定將 ${unordered.length} 筆未叫貨全部標為已處理？`)) return;
    setAckingAll(true);
    try {
      const res = await api('/api/inventory/order-alerts/ack-all', {
        method: 'POST',
        body: JSON.stringify({
          rows: unordered.map(u => ({ order_date: u.order_date, vendor_id: u.vendor_id })),
          operator: name,
        }),
      });
      if (res.success) load();
      else alert(res.error || '處理失敗');
    } catch (e) {
      alert('處理失敗');
    } finally {
      setAckingAll(false);
    }
  };

  const hasUncounted = uncounted.length > 0;
  const hasUnordered = unordered.length > 0;
  const hasBelowMin = belowMin.length > 0;
  if (loading && !hasUncounted && !hasUnordered && !hasBelowMin) return null;
  if (!hasUncounted && !hasUnordered && !hasBelowMin) return null;

  const box = (title, color, children) => (
    <div style={{
      marginBottom: 10, borderRadius: 10, border: `1px solid ${color.border}`,
      background: color.bg, overflow: 'hidden',
    }}>
      <div style={{
        padding: '8px 12px', fontWeight: 800, fontSize: 14, color: color.title,
        borderBottom: `1px solid ${color.border}`,
      }}>
        {title}
      </div>
      <div style={{ padding: '8px 12px' }}>{children}</div>
    </div>
  );

  // 未點貨依日期分組
  const uncountedByDate = {};
  uncounted.forEach((u) => {
    if (!uncountedByDate[u.date]) uncountedByDate[u.date] = [];
    uncountedByDate[u.date].push(u);
  });

  return (
    <div style={{ marginBottom: 14 }}>
      {hasUncounted && box('未點貨（近 7 天）', { bg: '#fffbeb', border: '#fcd34d', title: '#92400e' }, (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Object.keys(uncountedByDate).sort((a, b) => b.localeCompare(a)).map((d) => (
            <div key={d} style={{ fontSize: 14, color: '#78350f', lineHeight: 1.5 }}>
              <strong>{formatYmdLabel(d)}</strong>
              {' '}
              {uncountedByDate[d].map(u => u.name).join('、')}
            </div>
          ))}
        </div>
      ))}

      {hasUnordered && box((
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span>未叫貨（{unordered.length}）</span>
          <button
            type="button"
            onClick={ackAll}
            disabled={ackingAll}
            style={{
              padding: '3px 12px', borderRadius: 6, border: '1px solid #b91c1c',
              background: '#b91c1c', color: '#fff', fontWeight: 700, fontSize: 13,
              cursor: ackingAll ? 'wait' : 'pointer', opacity: ackingAll ? 0.6 : 1,
            }}
          >
            {ackingAll ? '處理中…' : '全部已處理'}
          </button>
        </div>
      ), { bg: '#fef2f2', border: '#fca5a5', title: '#991b1b' }, (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {unordered.map((u) => (
            <div key={`${u.order_date}-${u.vendor_id}`} style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              fontSize: 14, color: '#7f1d1d',
            }}>
              <span>
                {formatYmdLabel(u.order_date)} {u.vendor} 未叫貨
                {u.item_count != null ? `（${u.item_count} 項）` : ''}
              </span>
              <button
                type="button"
                onClick={() => ack(u)}
                style={{
                  padding: '3px 12px', borderRadius: 6, border: '1px solid #f87171',
                  background: '#fff', color: '#b91c1c', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                已處理
              </button>
            </div>
          ))}
        </div>
      ))}

      {hasBelowMin && box('未達低消', { bg: '#eff6ff', border: '#93c5fd', title: '#1e40af' }, (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {belowMin.map((b) => (
            <div key={b.vendor_id || b.vendor} style={{ fontSize: 14, color: '#1e3a8a', lineHeight: 1.5 }}>
              <strong>{b.vendor}</strong>
              {' '}目前 {formatMoney(b.amount)} / 低消 {formatMoney(b.min_order_amount)}
              ，差 {formatMoney(Math.max(0, b.min_order_amount - b.amount))}
              {b.missing_price && (
                <span style={{ marginLeft: 8, color: '#b45309', fontSize: 13 }}>
                  部分品項無單價，金額僅供參考
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
